import {
  AdminTableContainer,
  AdminTableHeader,
  Button,
  EmptyState,
  SectionHeading,
} from "@simplicity/ui";
import { useState } from "react";
import type { ConfigRevision } from "../../../configTypes";
import { clusterRevisions, type SaveEvent } from "./clusterRevisions";
import { ConnectorDiffSheet } from "./ConnectorDiffSheet";
import { fieldLabel } from "./connectorFieldLabels";

interface VersionHistoryListProps {
  revisions: ConfigRevision[];
  currentVars: Record<string, unknown>;
  onRestoreVars: (vars: Record<string, unknown>) => void;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function summarizeFields(saveEvent: SaveEvent): string {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const r of saveEvent.revisions) {
    if (seen.has(r.field)) continue;
    seen.add(r.field);
    labels.push(fieldLabel(r.field));
  }
  return labels.join(", ");
}

function SaveEventTableRow({ saveEvent, onOpen }: { saveEvent: SaveEvent; onOpen: () => void }) {
  return (
    <tr>
      <td className="col-muted">{formatTimestamp(saveEvent.createdAt)}</td>
      <td className="col-muted">{saveEvent.createdByEmail ?? "system"}</td>
      <td>{summarizeFields(saveEvent)}</td>
      <td className="text-right">
        <Button type="button" size="sm" variant="outline" onClick={onOpen}>
          View diff
        </Button>
      </td>
    </tr>
  );
}

export function VersionHistoryList({
  revisions,
  currentVars,
  onRestoreVars,
}: VersionHistoryListProps) {
  const [activeEvent, setActiveEvent] = useState<SaveEvent | null>(null);
  const saveEvents = clusterRevisions(revisions);

  return (
    <>
      <AdminTableContainer aria-label="Version history">
        <div className="admin-table-header">
          <SectionHeading size="lg" as="h2">
            Version History
          </SectionHeading>
        </div>
        {saveEvents.length === 0 ? (
          <EmptyState
            title="No saved changes yet"
            description="Each time you save edits to this connector, a snapshot is stored here. You can view the diff for any change and restore previous values."
          />
        ) : (
          <div className="max-h-[220px] overflow-y-auto">
            <table className="admin-table">
              <AdminTableHeader>
                <tr>
                  <th>When</th>
                  <th>By</th>
                  <th>Changed</th>
                  <th></th>
                </tr>
              </AdminTableHeader>
              <tbody>
                {saveEvents.map((saveEvent) => (
                  <SaveEventTableRow
                    key={saveEvent.id}
                    saveEvent={saveEvent}
                    onOpen={() => setActiveEvent(saveEvent)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminTableContainer>
      <ConnectorDiffSheet
        saveEvent={activeEvent}
        currentVars={currentVars}
        open={activeEvent !== null}
        onOpenChange={(open) => {
          if (!open) setActiveEvent(null);
        }}
        onRestore={onRestoreVars}
      />
    </>
  );
}
